import { Fragment, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { LightBulbIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { submitChangeRequest } from '../lib/changeRequests';

const INITIAL_FORM = {
  kind: '',
  title: '',
  description: '',
  // Feature
  problemSolved: '',
  howItWorks: '',
  // Bug
  whereInApp: '',
  whatDoing: '',
  whatHappened: '',
  whatExpected: '',
  // Notification
  notifyOnUpdate: true,
};

function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i === current ? 'w-8 bg-NBNA-primary' : i < current ? 'w-2 bg-NBNA-primary/60' : 'w-2 bg-gray-300'
          }`}
        />
      ))}
    </div>
  );
}

export default function ChangeRequestModal({ isOpen, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const canAdvance = useMemo(() => {
    if (step === 0) return form.kind;
    return true;
  }, [step, form.kind]);

  const handleClose = () => {
    setStep(0);
    setForm(INITIAL_FORM);
    setError('');
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await submitChangeRequest({
        kind: form.kind,
        title: form.title.trim(),
        description: form.description.trim(),
        problemSolved: form.problemSolved.trim() || null,
        howItWorks: form.howItWorks.trim() || null,
        whereInApp: form.whereInApp.trim() || null,
        whatDoing: form.whatDoing.trim() || null,
        whatHappened: form.whatHappened.trim() || null,
        whatExpected: form.whatExpected.trim() || null,
        notifyOnUpdate: form.notifyOnUpdate,
      });
      setStep(2);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="px-6 py-5">
                  {/* Close button */}
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                    {step === 2 ? 'Request Submitted' : 'Submit Feedback'}
                  </Dialog.Title>

                  {step < 2 && <StepIndicator current={step} total={2} />}

                  {/* ---- Step 0: Type Selection Only ---- */}
                  {step === 0 && (
                    <div className="mt-4 space-y-5">
                      <p className="text-sm text-gray-600">
                        How can we improve your experience? Choose one to get started.
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setForm((p) => ({ ...p, kind: 'feature_request' }));
                            setStep(1);
                          }}
                          className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
                            form.kind === 'feature_request'
                              ? 'border-NBNA-primary bg-NBNA-primary/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <LightBulbIcon className="h-6 w-6 text-NBNA-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium text-gray-900">I have an idea</span>
                            <p className="text-xs text-gray-500 mt-1">Suggest a new feature or improvement</p>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setForm((p) => ({ ...p, kind: 'bug_report' }));
                            setStep(1);
                          }}
                          className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
                            form.kind === 'bug_report'
                              ? 'border-NBNA-primary bg-NBNA-primary/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <ExclamationTriangleIcon className="h-6 w-6 text-NBNA-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium text-gray-900">Something's not right</span>
                            <p className="text-xs text-gray-500 mt-1">Report a problem you've encountered</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ---- Step 1: Details ---- */}
                  {step === 1 && (
                    <div className="mt-4 space-y-4">
                      {form.kind === 'feature_request' ? (
                        <>
                          <p className="text-sm text-gray-600">
                            A few more details help us understand your idea better.
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-title">
                              What would you like?
                            </label>
                            <input
                              id="cr-title"
                              type="text"
                              className="input-field w-full"
                              placeholder="e.g. Ability to export referrals"
                              value={form.title}
                              onChange={set('title')}
                              maxLength={256}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-description">
                              Tell us a bit more
                            </label>
                            <textarea
                              id="cr-description"
                              className="input-field w-full"
                              rows={2}
                              placeholder="Describe what you'd like..."
                              value={form.description}
                              onChange={set('description')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-problemSolved">
                              What problem would this solve for you?
                            </label>
                            <textarea
                              id="cr-problemSolved"
                              className="input-field w-full"
                              rows={3}
                              placeholder="Right now I have to... and it would be easier if..."
                              value={form.problemSolved}
                              onChange={set('problemSolved')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-howItWorks">
                              How do you imagine this working in the app?
                            </label>
                            <textarea
                              id="cr-howItWorks"
                              className="input-field w-full"
                              rows={3}
                              placeholder="I'd click on... and then it would..."
                              value={form.howItWorks}
                              onChange={set('howItWorks')}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-title">
                              What went wrong?
                            </label>
                            <input
                              id="cr-title"
                              type="text"
                              className="input-field w-full"
                              placeholder="e.g. Unable to update my profile photo"
                              value={form.title}
                              onChange={set('title')}
                              maxLength={256}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-description">
                              Tell us a bit more
                            </label>
                            <textarea
                              id="cr-description"
                              className="input-field w-full"
                              rows={2}
                              placeholder="Describe what happened..."
                              value={form.description}
                              onChange={set('description')}
                            />
                          </div>
                          <p className="text-sm text-gray-600">
                            Help us track down the issue so we can fix it.
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-whereInApp">
                              Where were you in the app?
                            </label>
                            <input
                              id="cr-whereInApp"
                              type="text"
                              className="input-field w-full"
                              placeholder="e.g. Settings page, Member directory, Events"
                              value={form.whereInApp}
                              onChange={set('whereInApp')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-whatDoing">
                              What were you trying to do?
                            </label>
                            <textarea
                              id="cr-whatDoing"
                              className="input-field w-full"
                              rows={2}
                              placeholder="I was trying to..."
                              value={form.whatDoing}
                              onChange={set('whatDoing')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-whatHappened">
                              What happened?
                            </label>
                            <textarea
                              id="cr-whatHappened"
                              className="input-field w-full"
                              rows={2}
                              placeholder="The page showed... / nothing happened / I got an error..."
                              value={form.whatHappened}
                              onChange={set('whatHappened')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cr-whatExpected">
                              What did you expect to happen?
                            </label>
                            <textarea
                              id="cr-whatExpected"
                              className="input-field w-full"
                              rows={2}
                              placeholder="I expected it to..."
                              value={form.whatExpected}
                              onChange={set('whatExpected')}
                            />
                          </div>
                        </>
                      )}

                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.notifyOnUpdate}
                          onChange={set('notifyOnUpdate')}
                          className="h-4 w-4 rounded border-gray-300 text-NBNA-primary focus:ring-NBNA-primary"
                        />
                        Keep me updated on this request
                      </label>

                      {error && (
                        <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
                      )}

                      <div className="flex justify-between">
                        <button type="button" className="btn-secondary" onClick={() => setStep(0)}>
                          Back
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={submitting}
                          onClick={handleSubmit}
                        >
                          {submitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ---- Step 2: Confirmation ---- */}
                  {step === 2 && (
                    <div className="mt-6 text-center space-y-4 py-4">
                      <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
                      <h3 className="text-lg font-medium text-gray-900">Thank you for your feedback!</h3>
                      <p className="text-sm text-gray-600 max-w-md mx-auto">
                        We've received your {form.kind === 'feature_request' ? 'feature request' : 'bug report'}.
                        {form.notifyOnUpdate
                          ? " We'll keep you posted as things progress."
                          : ''}
                      </p>
                      <button type="button" className="btn-primary" onClick={handleClose}>
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
